import React, { useState } from 'react';
import { Bell, Users, Settings, TrendingUp, PlusCircle, X, Edit, Trash2, Shield, LogOut, UserCog, Eye, EyeOff, Paperclip, User } from 'lucide-react';
import FileAttachmentUpload from './components/FileAttachmentUpload';
import AttachmentsDisplay from './components/AttachmentsDisplay';
import { 
  ReactionSummary, 
  ReactionPicker, 
  ReactionsListModal, 
  getReactionIcon 
} from './components/shared/ReactionComponents';
import ViewsDisplay from './components/ViewsDisplay';
import { getDisplayName } from './utils/userUtils';
import { formatDate } from './utils/dateUtils';
import { User as UserModel } from './models/User';
import { useTimelinePosts } from './hooks/useTimelinePosts';
import { useAuth } from './hooks/useAuth';
import { TimelinePost, Attachment } from './models/Content';
import Spinner from './components/Spinner';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Card } from './components/ui/card';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { format } from 'date-fns';
import { Edit2, ThumbsUp, MessageSquare, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SendQuestionButton from './components/shared/SendQuestionButton';
import PostActions from './components/shared/PostActions';

interface TimelineProps {
  user: UserModel;
  users: UserModel[];
  onLogout: () => void;
}

export const Timeline: React.FC<TimelineProps> = ({ user, users, onLogout }) => {
  const {
    items: posts,
    isLoading,
    error,
    showNewForm,
    setShowNewForm,
    editingId,
    setEditingId,
    formData,
    setFormData,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleInputChange,
    reloadPosts,
    getReactionsForTimelinePost,
    handleAddReaction,
    handleRemoveReaction,
  } = useTimelinePosts();
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [selectedFilter, setSelectedFilter] = useState<'All' | 'Newsletter' | 'Announcement'>('All');

  // Per-post state
  const [showFull, setShowFull] = useState<{[id: string]: boolean}>({});
  const [showPicker, setShowPicker] = useState<{[id: string]: boolean}>({});
  const [showList, setShowList] = useState<{[id: string]: boolean}>({});
  const [optimisticHidden, setOptimisticHidden] = useState<{[id: string]: boolean}>({});

  // Local state for form
  const [localForm, setLocalForm] = useState<{
    title: string;
    content: string;
    timelinePostCategory: 'Update' | 'Newsletter' | 'Announcement';
  }>({ title: '', content: '', timelinePostCategory: 'Update' });
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>([]);

  // Filter posts based on selected filter
  const filteredPosts = posts.filter(post => {
    if (selectedFilter === 'All') return true;
    return post.timelinePostCategory === selectedFilter;
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-gray-500">Please log in to view timeline</div>
      </div>
    );
  }

  const handleHideClick = async (post: TimelinePost) => {
    try {
      // Optimistically update UI
      setOptimisticHidden(prev => ({ ...prev, [post.id!]: !post.hidden }));
      await handleEdit(post.id!, { ...post, hidden: !post.hidden });
    } catch (error) {
      // Revert UI change if error
      setOptimisticHidden(prev => ({ ...prev, [post.id!]: !!post.hidden }));
      console.error('Failed to hide/unhide post:', error);
    }
  };

  // Handlers for per-post state
  const handleShowFull = (id: string) => setShowFull(prev => ({ ...prev, [id]: true }));
  const handleShowPicker = (id: string, value: boolean) => setShowPicker(prev => ({ ...prev, [id]: value }));
  const handleShowList = (id: string, value: boolean) => setShowList(prev => ({ ...prev, [id]: value }));

  // When opening new post form
  const openNewForm = () => {
    setShowNewForm(true);
    setEditingId(null);
    setLocalForm({ title: '', content: '', timelinePostCategory: 'Update' });
  };

  // When opening edit form
  const openEditForm = (post: TimelinePost) => {
    setEditingId(post.id!);
    setShowNewForm(false);
    setLocalForm({ 
      title: post.title || '', 
      content: post.content || '', 
      timelinePostCategory: post.timelinePostCategory || 'Update' 
    });
    setLocalAttachments(post.attachments || []);
  };

  // Handle input change
  const handleLocalInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocalForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle submit for local form
  const handleSubmitLocal = async () => {
    // Create a fake event to pass to handleSubmit
    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
    const directFormData = { 
      title: localForm.title, 
      content: localForm.content, 
      timelinePostCategory: localForm.timelinePostCategory,
      attachments: localAttachments 
    };
    await handleSubmit(fakeEvent, directFormData);
    setLocalForm({ title: '', content: '', timelinePostCategory: 'Update' });
    setLocalAttachments([]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Timeline</h1>
            <p className="text-slate-600 mt-2">Stay updated with company news and announcements</p>
          </div>
          {user && (isAdmin || isSuperAdmin) && (
            <Button 
              onClick={openNewForm}
              variant="outline"
              className="border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-auto px-4 py-1 text-sm font-medium rounded-lg"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              New Post
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="border-b border-slate-200 mb-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide" aria-label="Tabs">
            <button
              onClick={() => setSelectedFilter('All')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                selectedFilter === 'All'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              All
            </button>
            <button
              onClick={() => setSelectedFilter('Newsletter')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                selectedFilter === 'Newsletter'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Bell className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Newsletters</span>
              <span className="sm:hidden">News</span>
            </button>
            <button
              onClick={() => setSelectedFilter('Announcement')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                selectedFilter === 'Announcement'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Edit2 className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Announcements</span>
              <span className="sm:hidden">Announce</span>
            </button>
          </nav>
        </div>

        {showNewForm && (
          <Card className="overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Create New Post</h3>
            <form onSubmit={e => { e.preventDefault(); handleSubmitLocal(); }}>
              <div className="space-y-4">
                <Input
                  name="title"
                  value={localForm.title}
                  onChange={handleLocalInputChange}
                  placeholder="Post title"
                  required
                />
                <Textarea
                  name="content"
                  value={localForm.content}
                  onChange={handleLocalInputChange}
                  placeholder="What's on your mind?"
                  required
                />
                <select
                  name="timelinePostCategory"
                  value={localForm.timelinePostCategory}
                  onChange={handleLocalInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Update">Update</option>
                  <option value="Newsletter">Newsletter</option>
                  <option value="Announcement">Announcement</option>
                </select>
                <FileAttachmentUpload
                  attachments={localAttachments}
                  setAttachments={setLocalAttachments}
                  label="Attachments"
                />
                <div className="flex justify-end gap-3">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => { setShowNewForm(false); setLocalAttachments([]); }}
                    className="h-8 px-3 text-sm"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    variant="outline"
                    className="h-8 px-3 text-sm"
                  >
                    Post
                  </Button>
                </div>
              </div>
            </form>
          </Card>
        )}

        <div className="space-y-6">
          {filteredPosts.map((post) => {
            // Find author user object
            const author = users.find(u => u.id === post.authorId);
            const authorName = author ? getDisplayName(author) : post.authorId;
            // Format date/time
            const postDate = post.created_at && post.created_at.toDate ? post.created_at.toDate() : new Date();
            const formattedDate = format(postDate, 'yyyy-MM-dd HH:mm');
            // Truncate content
            const content = post.content || '';
            const title = post.title || 'Untitled Post';
            const words = content.split(/\s+/);
            const isTruncated = words.length > 40;
            const truncatedText = isTruncated ? words.slice(0, 40).join(' ') + '...' : content;
            // Reactions
            const reactions = getReactionsForTimelinePost ? getReactionsForTimelinePost(post.id!) : [];
            const userReaction = reactions.find(r => r.userId === user.id && ['like','love','celebrate','insightful'].includes(r.reactionType));
            // Reaction handlers
            const handleReact = (type: 'like' | 'love' | 'celebrate' | 'insightful' | 'support') => {
              if (type === 'support') return; // Ignore 'support' as it's not used
              handleAddReaction(post.id!, type, user);
              handleShowPicker(post.id!, false);
            };
            const handleRemove = () => {
              handleRemoveReaction(post.id!, user);
            };
            // Send Question navigation
            const handleSendQuestion = () => {
              navigate(`/inbox?user=${post.authorId}`);
            };
            // Render edit form if editingId matches this post
            if (editingId === post.id) {
              return (
                <Card key={post.id} className="overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl p-6 lg:p-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Edit Post</h3>
                  <form
                    onSubmit={e => { e.preventDefault(); handleEdit(post.id!, { title: localForm.title || '', content: localForm.content || '', timelinePostCategory: localForm.timelinePostCategory, attachments: localAttachments }); }}
                    className="space-y-4"
                  >
                    <Input
                      name="title"
                      value={localForm.title}
                      onChange={handleLocalInputChange}
                      placeholder="Post title"
                      required
                    />
                    <Textarea
                      name="content"
                      value={localForm.content}
                      onChange={handleLocalInputChange}
                      placeholder="What's on your mind?"
                      required
                    />
                    <select
                      name="timelinePostCategory"
                      value={localForm.timelinePostCategory}
                      onChange={handleLocalInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Update">Update</option>
                      <option value="Newsletter">Newsletter</option>
                      <option value="Announcement">Announcement</option>
                    </select>
                    <FileAttachmentUpload attachments={localAttachments} setAttachments={setLocalAttachments} />
                    <div className="flex justify-end gap-3">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={() => { setEditingId(null); setLocalForm({ title: '', content: '', timelinePostCategory: 'Update' }); setLocalAttachments([]); }}
                        className="h-8 px-3 text-sm"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        variant="outline"
                        className="h-8 px-3 text-sm"
                      >
                        Update Post
                      </Button>
                    </div>
                  </form>
                </Card>
              );
            }
            return (
              <Card key={post.id} className={`overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl ${
                post.timelinePostCategory === 'Newsletter' 
                  ? 'border-l-4 border-l-red-400' 
                  : post.timelinePostCategory === 'Announcement' 
                    ? 'border-l-4 border-l-orange-400' 
                    : ''
              }`}>
                <div className="p-6 lg:p-8">
                  {/* Header with Actions */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6">
                    <div className="flex-1 mb-4 sm:mb-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          post.timelinePostCategory === 'Newsletter' 
                            ? 'bg-red-100 text-red-800 border border-red-200' 
                            : post.timelinePostCategory === 'Announcement' 
                              ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                              : 'bg-slate-100 text-slate-800 border border-slate-200'
                        }`}>
                          {post.timelinePostCategory}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-slate-600">
                        <User className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="font-medium">{authorName}</span>
                        <span className="mx-2 text-slate-400">•</span>
                        <span>{format(postDate, 'MMM dd, yyyy')}</span>
                      </div>
                    </div>
                    {user && (
                      <div className="flex-shrink-0">
                        <PostActions
                          isAdmin={isAdmin}
                          isSuperAdmin={isSuperAdmin}
                          hidden={optimisticHidden[post.id!] ?? post.hidden}
                          onEdit={() => openEditForm(post)}
                          onHide={() => handleHideClick(post)}
                          onUnhide={() => handleHideClick(post)}
                          onDelete={() => handleDelete(post.id!)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="mb-6">
                    {isTruncated && !showFull[post.id!] ? (
                      <div className="text-slate-700 leading-relaxed">
                        {truncatedText}
                        <button
                          onClick={() => handleShowFull(post.id!)}
                          className="text-blue-600 hover:text-blue-700 ml-1 font-medium"
                        >
                          Read More
                        </button>
                      </div>
                    ) : (
                      <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {content}
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  {post.attachments && post.attachments.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                        <Paperclip className="w-4 h-4 mr-2 text-slate-500" />
                        Attachments
                      </h3>
                      <AttachmentsDisplay attachments={post.attachments} />
                    </div>
                  )}

                  {/* Action Footer */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center space-x-2 relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex items-center text-blue-600 hover:text-blue-700 font-medium h-8 px-3 text-sm"
                            onClick={e => {
                              e.stopPropagation();
                              handleShowPicker(post.id!, !showPicker[post.id!]);
                            }}
                          >
                            <ThumbsUp className="w-4 h-4 mr-2" />
                            React
                          </Button>
                          
                          {/* Reaction picker */}
                          {showPicker[post.id!] && (
                            <div className="absolute bottom-full left-0 mb-2 z-10">
                              <ReactionPicker
                                onReact={handleReact}
                                onClose={() => handleShowPicker(post.id!, false)}
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Reaction summary */}
                        {reactions.length > 0 && (
                          (isAdmin || isSuperAdmin) ? (
                            <ReactionSummary 
                              reactions={reactions}
                              getReactionType={r => r.reactionType as any}
                              onShowDetails={() => handleShowList(post.id!, true)}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-600 font-medium">
                                {reactions.length} reactions
                              </span>
                              <div className="flex items-center gap-1">
                                {['like','love','celebrate','insightful'].map(type => {
                                  const count = reactions.filter(r => r.reactionType === type).length;
                                  return count > 0 ? (
                                    <span key={type} className="flex items-center gap-1">
                                      {getReactionIcon(type as any, 16)}
                                      <span className="text-xs font-medium text-slate-600">{count}</span>
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      
                      <SendQuestionButton authorId={post.authorId} source="Timeline" />
                    </div>
                  </div>

                  {/* Reactions list modal */}
                  {showList[post.id!] && (
                    <ReactionsListModal
                      reactions={reactions}
                      getReactionType={r => r.reactionType as any}
                      onClose={() => handleShowList(post.id!, false)}
                      users={users}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline; 